import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { PlaceAutocomplete, TripSkeleton, EmptyState, IC } from "../shared";
import TripCard from "./TripCard";

// ── Find Screen ───────────────────────────────────────────────────────────────
const FindTrip = ({ currentUser, setDetail }) => {
  const [from, setFrom]             = useState("");
  const [to, setTo]                 = useState("");
  const [fromCoords, setFromCoords] = useState(null);
  const [trips, setTrips]           = useState([]);
  const [myReqs, setMyReqs]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterDate, setFilterDate]     = useState("");
  const dateRef = useRef(null);
  const [routeMatches, setRouteMatches] = useState([]);
  const [checkingRoutes, setCheckingRoutes] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "trips"), where("status", "==", "active"));
    const unsub = onSnapshot(q, snap => {
      const now = new Date();
      setTrips(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.driverId !== currentUser.id && new Date(t.datetime) > now));
      setLoading(false);
    });
    return unsub;
  }, [currentUser.id]);

  useEffect(() => {
    const q = query(collection(db, "requests"), where("passengerId", "==", currentUser.id));
    const unsub = onSnapshot(q, snap => setMyReqs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [currentUser.id]);

  // Route-based matching
  useEffect(() => {
    if (!fromCoords) { setRouteMatches([]); return; }
    const candidates = trips.filter(t => t.fromLat && !t.from.toLowerCase().includes(from.toLowerCase()));
    if (candidates.length === 0) { setRouteMatches([]); return; }
    setCheckingRoutes(true);
    const check = async (trip) => {
      try {
        const [orig, via] = await Promise.all([
          fetch(`https://router.project-osrm.org/route/v1/driving/${trip.fromLon},${trip.fromLat};${trip.toLon},${trip.toLat}?overview=false`).then(r => r.json()),
          fetch(`https://router.project-osrm.org/route/v1/driving/${trip.fromLon},${trip.fromLat};${fromCoords.lon},${fromCoords.lat};${trip.toLon},${trip.toLat}?overview=false`).then(r => r.json()),
        ]);
        if (!orig.routes?.[0] || !via.routes?.[0]) return null;
        return (via.routes[0].distance / orig.routes[0].distance) < 1.12 ? trip : null;
      } catch { return null; }
    };
    Promise.all(candidates.map(check)).then(results => {
      setRouteMatches(results.filter(Boolean));
      setCheckingRoutes(false);
    });
  }, [fromCoords, trips, from]); // eslint-disable-line react-hooks/exhaustive-deps

  // For each trip, find the best (most relevant) request: accepted > pending > cancelled/rejected
  const bestReqForTrip = (tripId) => {
    const reqs = myReqs.filter(r => r.tripId === tripId);
    return reqs.find(r => r.status === "accepted") || reqs.find(r => r.status === "pending") || reqs[0] || null;
  };

  // Filtered trips
  // Only hide trips where the BEST request is rejected (not if there's a newer pending/accepted one)
  const rejectedTripIds = new Set(myReqs.filter(r => r.status === "rejected").map(r => r.tripId));
  const hasActiveReq = new Set(myReqs.filter(r => r.status === "pending" || r.status === "accepted").map(r => r.tripId));
  hasActiveReq.forEach(id => rejectedTripIds.delete(id));
  const matchesDate = (trip) => {
    if (!filterDate) return true;
    const td = new Date(trip.datetime);
    const fd = new Date(filterDate);
    return td.getFullYear() === fd.getFullYear() && td.getMonth() === fd.getMonth() && td.getDate() === fd.getDate();
  };

  const directMatches  = trips.filter(t => {
    if (rejectedTripIds.has(t.id)) return false;
    if (!matchesDate(t)) return false;
    const fm = !from || t.from.toLowerCase().includes(from.toLowerCase());
    const tm = !to   || t.to.toLowerCase().includes(to.toLowerCase());
    return fm && tm;
  });
  const partialMatches = (from && to) ? trips.filter(t => {
    if (rejectedTripIds.has(t.id)) return false;
    if (!matchesDate(t)) return false;
    const fm = t.from.toLowerCase().includes(from.toLowerCase());
    const tm = !t.to.toLowerCase().includes(to.toLowerCase());
    return fm && tm && !directMatches.includes(t);
  }) : [];
  const onRouteMatches = routeMatches.filter(t => !directMatches.includes(t) && !partialMatches.includes(t) && !rejectedTripIds.has(t.id) && matchesDate(t));
  const filtered = [...directMatches, ...partialMatches];
  const totalCount = filtered.length + onRouteMatches.length;

  const swapLocations = () => {
    const tmpFrom = from; const tmpTo = to; const tmpCoords = fromCoords;
    setFrom(tmpTo); setTo(tmpFrom); setFromCoords(null);
  };

  // Shared search bar
  const searchBar = (
    <div className="igo-find-search" style={{ background: "var(--card)", borderRadius: 18, border: "1.5px solid var(--border)", boxShadow: "var(--shadow-lg)", overflow: "visible" }}>
      {/* ── Location section (From + To) with floating swap button ── */}
      <div style={{ position: "relative", padding: "14px 14px 14px 14px" }}>
        {/* Row 1: FROM */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingRight: 46 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--accent)", boxShadow: "0 0 0 3px rgba(43,91,224,.15)", flexShrink: 0 }}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <PlaceAutocomplete placeholder="Pickup location" value={from} onChange={v => { setFrom(v); setFromCoords(null); }} onCoords={setFromCoords}/>
          </div>
        </div>
        {/* Dashed divider — starts after dot+gap, stops before swap button */}
        <div style={{ borderTop: "1.5px dashed rgba(43,91,224,.15)", margin: "8px 56px 8px 24px" }}/>
        {/* Row 2: TO */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingRight: 46 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--accent2)", boxShadow: "0 0 0 3px rgba(43,91,224,.15)", flexShrink: 0 }}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <PlaceAutocomplete placeholder="Where to?" value={to} onChange={v => setTo(v)}/>
          </div>
        </div>
        {/* Swap button — floating right */}
        <button onClick={swapLocations} className="btn-press" style={{ position: "absolute", top: "50%", right: 14, transform: "translateY(-50%)", width: 34, height: 34, borderRadius: "50%", background: "var(--warm)", border: "1.5px solid var(--border)", boxShadow: "var(--shadow)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "transform .15s", color: "var(--accent)" }}
          onMouseEnter={e => e.currentTarget.style.transform = "translateY(-50%) scale(1.05)"}
          onMouseLeave={e => e.currentTarget.style.transform = "translateY(-50%)"}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4"/></svg>
        </button>
      </div>
      {/* ── Solid divider ── */}
      <div style={{ height: 1, background: "var(--border)" }}/>
      {/* ── When section — hidden date input triggered by tapping the row ── */}
      <div
        onClick={() => dateRef.current?.showPicker?.() || dateRef.current?.focus()}
        style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, minHeight: 48, cursor: "pointer" }}
      >
        <svg style={{ width: 14, height: 14, color: "var(--muted)", flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: filterDate ? "var(--ink)" : "var(--muted)" }}>
          {filterDate ? new Date(filterDate + "T00:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" }) : "When?"}
        </span>
        {filterDate && (
          <button onClick={e => { e.stopPropagation(); setFilterDate(""); }} style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--warm)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", flexShrink: 0, fontSize: 12 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        )}
        <input
          ref={dateRef}
          type="date"
          value={filterDate}
          onChange={e => setFilterDate(e.target.value)}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
        />
      </div>
      {/* Override PlaceAutocomplete inner styles: hide pin icons, remove input borders, hide calendar picker */}
      <style>{`
        .igo-find-search div[style*="position"] > span:first-child { display: none !important; }
        .igo-find-search input { border: none !important; background: transparent !important; box-shadow: none !important; padding: 4px 0 !important; font-size: 14px !important; border-radius: 0 !important; }
        .igo-find-search input:focus { border: none !important; box-shadow: none !important; }
        .igo-find-search input[type="date"] { position: absolute !important; opacity: 0 !important; pointer-events: none !important; width: 0 !important; height: 0 !important; }
      `}</style>
    </div>
  );

  // Trip list content
  const tripList = (
    <>
      {loading ? <TripSkeleton count={3}/> : totalCount === 0 ? (
        <EmptyState
          icon={IC.pin}
          title="No rides found"
          description="Try different locations or check back later for new rides"
        />
      ) : (
        <>
          {totalCount > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>Available rides{filterDate ? ` for ${new Date(filterDate).toLocaleDateString("en", { month: "short", day: "numeric" })}` : ""}</span>
              <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 11, color: "var(--accent)", padding: "3px 10px", background: "rgba(43,91,224,.08)", borderRadius: 99, border: "1px solid rgba(43,91,224,.2)", letterSpacing: "0.04em", textTransform: "uppercase" }}>{totalCount} ride{totalCount !== 1 ? "s" : ""}</span>
            </div>
          )}
          {filtered.map((trip, i) => (
            <div key={trip.id}>
              {partialMatches.includes(trip) && directMatches.length > 0 && partialMatches.indexOf(trip) === 0 && (
                <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, marginTop: 4 }}>May pass through your stop</div>
              )}
              <TripCard trip={trip} myReq={bestReqForTrip(trip.id)} onClick={() => setDetail(trip)} partial={partialMatches.includes(trip)} idx={i}/>
            </div>
          ))}
          {onRouteMatches.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, marginTop: filtered.length > 0 ? 16 : 0, display: "flex", alignItems: "center", gap: 6 }}>
                <span>Passes through your area</span>
                {checkingRoutes && <span style={{ width: 10, height: 10, border: "1.5px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>}
              </div>
              {onRouteMatches.map((trip, i) => (
                <TripCard key={trip.id} trip={trip} myReq={bestReqForTrip(trip.id)} onClick={() => setDetail(trip)} partial idx={filtered.length + i}/>
              ))}
            </>
          )}
          {checkingRoutes && onRouteMatches.length === 0 && (
            <div style={{ textAlign: "center", padding: "16px 0", color: "var(--muted)", fontSize: 13 }}>Checking nearby routes…</div>
          )}
        </>
      )}
    </>
  );

  return (
    <div className="screen" style={{ padding: 0 }}>
      {/* ── Mobile ── */}
      <div className="mobile-only" style={{ position: "absolute", inset: 0, overflowY: "auto", background: "var(--paper)" }}>
        {/* Hero banner */}
        <div style={{ position: "relative", padding: "40px 20px 28px", background: "linear-gradient(135deg, #1a2e6e 0%, #2b5be0 60%, #5a80f0 100%)", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -60, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)", filter: "blur(40px)" }}/>
          <div style={{ position: "absolute", bottom: -30, left: -20, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,.04)", filter: "blur(30px)" }}/>
          <h1 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 26, color: "#fff", lineHeight: 1.2, position: "relative" }}>Where are you going?</h1>
          <p style={{ color: "rgba(255,255,255,.65)", fontSize: 13, marginTop: 6, position: "relative" }}>Find affordable rides across Cyprus</p>
        </div>

        {/* Search card overlapping hero */}
        <div style={{ padding: "0 16px", marginTop: -14, position: "relative", zIndex: 2 }}>
          <div style={{ marginBottom: 20 }}>{searchBar}</div>
        </div>

        {/* Trip list */}
        <div style={{ padding: "0 16px 90px" }}>
          {tripList}
        </div>
      </div>

      {/* ── Desktop: Same layout as before ── */}
      <div className="desk-only" style={{ position: "absolute", inset: 0, overflowY: "auto" }}>
        <div className="desk-find-hero" style={{ margin: 0 }}>
          <div className="desk-find-hero-inner">
            <h1 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 36, color: "#fff", marginBottom: 4 }}>Where are you going?</h1>
            <p style={{ color: "rgba(255,255,255,.65)", fontSize: 15, marginBottom: 0 }}>Find affordable rides across Cyprus</p>
            <div className="desk-search-row">
              <div style={{ padding: "4px 0 4px 8px" }}><PlaceAutocomplete placeholder="From — e.g. Nicosia" value={from} onChange={v => { setFrom(v); setFromCoords(null); }} onCoords={setFromCoords}/></div>
              <div style={{ padding: "4px 0 4px 8px" }}><PlaceAutocomplete placeholder="To — e.g. Limassol" value={to} onChange={v => setTo(v)}/></div>
              <button className="desk-search-btn">Search</button>
            </div>
          </div>
        </div>
        <div className="desk-container" style={{ paddingTop: 32, paddingBottom: 40 }}>{tripList}</div>
      </div>
    </div>
  );
};

export default FindTrip;
